import { useState } from 'react';
import { Sparkles, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { chAiService } from '../services/aiService';

interface Props {
  projectId: string;
  postId?: string | null;
  title: string;
  platform: string;
  contentType: string;
  scheduledDate?: string | null;
  currentCaption: string;
  onSelect: (caption: string) => void;
}

export function AiCaptionPanel({ projectId, postId, title, platform, contentType, scheduledDate, currentCaption, onSelect }: Props) {
  const [generating, setGenerating] = useState(false);
  const [variations, setVariations] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [noToneProfile, setNoToneProfile] = useState(false);

  async function handleGenerate() {
    if (!title.trim()) {
      setError('Preenche o título primeiro.');
      return;
    }

    setGenerating(true);
    setError('');
    setVariations([]);

    try {
      const res = await chAiService.generateCaption({
        project_id: projectId,
        post_id: postId,
        title,
        platform,
        content_type: contentType,
        scheduled_date: scheduledDate,
        current_caption: currentCaption || null,
      });

      if (!res.success) {
        setError(res.error || 'Erro ao gerar caption');
        return;
      }

      setNoToneProfile(!res.tone_profile_set);
      const data = res.data;
      if (data && Array.isArray(data.variations)) {
        setVariations(data.variations);
      } else {
        setError('Resposta inesperada da IA');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar caption');
    } finally {
      setGenerating(false);
    }
  }

  if (variations.length === 0 && !generating && !error) {
    return (
      <button
        type="button"
        onClick={handleGenerate}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-700 hover:from-amber-100 hover:to-orange-100 transition-all"
      >
        <Sparkles className="w-3 h-3" />
        {currentCaption ? 'Melhorar' : 'Gerar caption'}
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {noToneProfile && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>Este projeto ainda não tem perfil de tom. Define-o nas Definições para melhores resultados.</span>
        </div>
      )}

      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs font-medium">
          {error}
        </div>
      )}

      {generating && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
          <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />
          <span className="text-xs text-gray-500">A gerar...</span>
        </div>
      )}

      {variations.length > 0 && (
        <>
          <div className="space-y-2">
            {variations.map((v, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => { onSelect(v); setVariations([]); }}
                className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-[var(--ch-ink)] hover:bg-gray-50 transition-all group"
              >
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-bold text-gray-300 mt-0.5">{idx + 1}</span>
                  <p className="text-xs text-gray-700 leading-relaxed flex-1">{v}</p>
                  <Check className="w-3.5 h-3.5 text-gray-300 group-hover:text-[var(--ch-ink)] flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-gray-500 hover:text-[var(--ch-ink)] transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
            Regenerar
          </button>
        </>
      )}
    </div>
  );
}
